/**
 * Where a policy is on its way to sold, as a track: the open stages, Issued, then Closed Won or
 * Closed Lost. Passed steps are filled, the current one is named in bold and marked for assistive
 * tech; Closed Won is the one green step, because won is what green means here.
 * Under a sale, the post-sale band: whether it is still on the books and when money moves next.
 */
import {
  policyStageLabel,
  postSaleStatusLabel,
  toUsDate,
  type PolicyStage,
  type PostSaleStatus,
} from "@tlcrm/shared";

import { money } from "../lib/format";

const OPEN: PolicyStage[] = ["qualification", "needs_analysis", "proposal", "issued"];

export function StageTrack({ stage }: { stage: PolicyStage }) {
  const final: PolicyStage = stage === "closed_lost" ? "closed_lost" : "closed_won";
  const steps = [...OPEN, final];
  const at = steps.indexOf(stage);
  return (
    <ol className="stage-track" aria-label="Stage">
      {steps.map((s, i) => (
        <li
          key={s}
          className={[
            i < at ? "is-done" : "",
            i === at ? "is-now" : "",
            i === at && s === "closed_won" ? "is-won" : "",
            i === at && s === "closed_lost" ? "is-lost" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          aria-current={i === at ? "step" : undefined}
        >
          {policyStageLabel(s)}
        </li>
      ))}
    </ol>
  );
}

export function PostSaleBand({
  status,
  effectiveDate,
  nextDraft,
  monthly,
  cancellationDate,
  cancellationReason,
}: {
  status: PostSaleStatus;
  effectiveDate: string | null;
  nextDraft: string | null;
  monthly: number | null;
  cancellationDate: string | null;
  cancellationReason: string | null;
}) {
  const tone = status === "active" ? "accent" : status === "cancelled" ? "brick" : "ochre";
  return (
    <div className={`postsale is-${status}`}>
      <span className={`mark mark-${tone}`}>{postSaleStatusLabel(status)}</span>
      {status === "active" ? (
        <>
          <span>
            Effective <b className="mono">{effectiveDate ? toUsDate(effectiveDate) : "not set"}</b>
          </span>
          <span>
            Next draft <b className="mono">{nextDraft ? toUsDate(nextDraft) : "not set"}</b>
          </span>
          {monthly !== null && (
            <span>
              <b className="mono">{money(monthly, true)}</b> a month
            </span>
          )}
        </>
      ) : (
        <>
          {cancellationDate && (
            <span>
              Since <b className="mono">{toUsDate(cancellationDate)}</b>
            </span>
          )}
          {cancellationReason && <span>{cancellationReason}</span>}
        </>
      )}
    </div>
  );
}
